import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/Logo";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
        if (password !== confirm) { setError("Passwords do not match."); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated. You can now log in.");
            navigate("/login", { replace: true });
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-8" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <div className="mb-8"><Logo /></div>
                <div className="label-mono mb-2">/ Password recovery</div>
                <h1 className="font-heading font-black text-3xl tracking-tighter mb-2">Set a new password</h1>
                <p className="text-sm text-avx-muted mb-8">Choose a new password for your account.</p>

                {!token ? (
                    <div className="panel p-6 text-sm text-avx-danger" data-testid="reset-invalid-token">
                        This reset link is invalid or incomplete. Please request a new one.
                        <div className="mt-4">
                            <Link to="/forgot-password" className="text-avx-blue font-mono uppercase tracking-[0.18em] text-xs">
                                Request new link →
                            </Link>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} data-testid="reset-password-form">
                        {error && (
                            <div className="mb-4 p-3 border border-avx-danger/40 bg-avx-danger/10 text-avx-danger text-sm rounded-sm" data-testid="reset-password-error">
                                {error}
                            </div>
                        )}
                        <div className="space-y-4 mb-6">
                            <div>
                                <Label htmlFor="password" className="label-mono">New password</Label>
                                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input"
                                    className="mt-2 bg-avx-surface border-avx-line h-11 focus:border-avx-blue focus:ring-1 focus:ring-avx-blue" />
                            </div>
                            <div>
                                <Label htmlFor="confirm" className="label-mono">Confirm password</Label>
                                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input"
                                    className="mt-2 bg-avx-surface border-avx-line h-11 focus:border-avx-blue focus:ring-1 focus:ring-avx-blue" />
                            </div>
                        </div>
                        <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                            className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                            {loading ? "Updating…" : "Update password"}
                        </button>
                        <div className="mt-6 text-xs">
                            <Link to="/login" className="text-avx-muted hover:text-avx-blue font-mono uppercase tracking-[0.18em]">
                                ← Back to login
                            </Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
